import { z } from "zod";

/**
 * UC-006 Mikroskupina invite form schema (Phase 28 / Milestone 20.1).
 *
 * Validation:
 *   - `name` — invited person display name, min 2 znaky (po trim).
 *   - `email` — RFC 5322 email format (Zod built-in `.email()`).
 *   - `relationship` — enum (family, friend, employee, other) per ANALYZE §2.6.
 *
 * Člen = osoba (User); SPZ se nepoužívá — karty se přiřadí přes holdera
 * po onboardingu (plán holder-unification Krok 2).
 *
 * Per DEC-011 anglické technické pojmenování. JSON payload posílaný backendu je
 * snake_case (REQ-API-012); mapping děláme manuálně v mutation hook.
 */

export const MICROGROUP_RELATIONSHIPS = [
  "family",
  "friend",
  "employee",
  "other",
] as const;
export type MicrogroupRelationship = (typeof MICROGROUP_RELATIONSHIPS)[number];

export const microgroupInviteSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "Jméno musí mít alespoň 2 znaky.")
    .max(120, "Jméno je příliš dlouhé."),
  email: z
    .string()
    .trim()
    .min(1, "E-mail je povinný.")
    .email("Neplatný formát e-mailu."),
  relationship: z.enum(MICROGROUP_RELATIONSHIPS, {
    message: "Vztah je povinný.",
  }),
});

export type MicrogroupInviteInput = z.infer<typeof microgroupInviteSchema>;

/**
 * Mapping z UI/RHF (`MicrogroupInviteInput`) na REST payload (snake_case).
 *
 * Backend endpoint: `POST /api/tenant/microgroups/{id}/invite` z Phase 19.
 */
export interface MicrogroupInviteApiPayload {
  invited_name: string;
  invited_email: string;
  relationship: MicrogroupRelationship;
}

export function toMicrogroupInviteApiPayload(
  input: MicrogroupInviteInput,
): MicrogroupInviteApiPayload {
  return {
    invited_name: input.name,
    invited_email: input.email,
    relationship: input.relationship,
  };
}

/** UI labels for relationship enum. */
export const MICROGROUP_RELATIONSHIP_LABELS: Record<MicrogroupRelationship, string> = {
  family: "Rodinný příslušník",
  friend: "Kamarád",
  employee: "Zaměstnanec / kolega",
  other: "Jiný vztah",
};
